import React from 'react';
import SEO from '../../components/ui/SEO';
import { Link } from 'react-router-dom';
import { Brain, Activity, Scale, HeartPulse, Stethoscope, ArrowRight, FileCheck } from 'lucide-react';
export default function Services() {
  const services = [{
    title: 'Mental Capacity Assessments UK',
    description: "Meticulous evaluations of an individual's capacity to make specific decisions, adhering to the Mental Capacity Act 2005. These assessments are vital for upholding individual rights and ensuring legal decisions are based on a profound understanding of mental capacity.",
    icon: <Activity className="h-12 w-12" data-id="element-1123" data-mp="25gf31exqqbx0" />,
    link: '/services/mental-capacity'
  }, {
    title: 'PTSD & Trauma Psychiatric Evaluations',
    description: "Expert assessments for Post-Traumatic Stress Disorder (PTSD) and Complex PTSD. Dr. Syed's reports adhere to international standards, providing sensitive and comprehensive evaluations of psychological distress stemming from traumatic experiences.",
    icon: <HeartPulse className="h-12 w-12" data-id="element-1124" data-mp="s22lsfaof8ri" />,
    link: '/services/trauma-ptsd'
  }, {
    title: 'ADHD & Autism Medico-Legal Assessments',
    description: "Comprehensive assessments for Attention-Deficit/Hyperactivity Disorder (ADHD) and Autism Spectrum Disorder (ASD). These insights are crucial in understanding how neurodevelopmental conditions may impact an individual's legal context or capacity.",
    icon: <Brain className="h-12 w-12" data-id="element-1125" data-mp="160ixylrb5rh1" />,
    link: '/services/neurodevelopmental'
  }, {
    title: 'Forensic Psychiatric Assessments UK',
    description: 'Critical insights into mental state within criminal justice contexts, including assessments regarding fitness to plead and stand trial, alongside thorough risk assessments concerning violence, self-harm, and self-neglect.',
    icon: <Scale className="h-12 w-12" data-id="element-1126" data-mp="3hqbhwff13i68" />,
    link: '/services/forensic-risk'
  }, {
    title: 'Psychiatric Reports for Courts & Tribunals',
    description: 'Expert psychiatric opinions tailored for diverse legal settings, including Immigration and Asylum Tribunals, Criminal Courts, Family Courts, Employment Tribunals, Civil Claims, and Mental Health Review Tribunals.',
    icon: <Stethoscope className="h-12 w-12" data-id="element-1127" data-mp="3e7vzjof9heea" />,
    link: '/services/specialist'
  }, {
    title: 'Clinical Negligence Psychiatric Assessments',
    description: 'Independent psychiatric assessments for clinical negligence cases, including causation, psychiatric injury, prognosis, and expert witness reporting.',
    icon: <FileCheck className="h-12 w-12" data-id="element-1128" data-mp="2qjw7ml67rhy4" />,
    link: '/services/clinical-negligence'
  }];
  return <main className="min-h-screen bg-white" data-id="element-1129" data-mp="2vi64d07l6qlu">
      <SEO title="Psychiatric Expert Witness Services | England and Wales" description="Forensic, capacity, PTSD, ADHD, immigration and clinical negligence psychiatric reports for legal proceedings. CPR Part 35 compliant." path="/services" keywords={['psychiatric expert witness services', 'medico-legal psychiatric assessments', 'psychiatric reports for legal proceedings']} imageAlt="Psychiatric expert witness services for legal proceedings" schema={{
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Psychiatric Expert Witness Services',
      itemListElement: services.map((service, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: service.title,
        url: `https://medicolegal-psychiatry.co.uk${service.link}`
      }))
    }} data-id="element-1130" data-mp="2hhvfu3tr1o3u" />
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-1131" data-mp="2qlk6to2fp1px">
        <div className="container mx-auto px-4" data-id="element-1132" data-mp="34lbj9kdaya1t">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-4 text-center" data-id="element-1133" data-mp="2zlgsrhxfvmha">
            Psychiatric Medico-Legal Services UK | Expert Reports
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-1134" data-mp="3jlh1eou6rm5m">
            Dr. Syed provides comprehensive Medico-Legal services for
            solicitors, barristers, and courts, guided by clarity, compassion,
            and expertise.
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-1135" data-mp="1betxqqh2psi">
        <div className="container mx-auto px-4" data-id="element-1136" data-mp="30p7h3jsoxzd6">
          <div className="max-w-3xl mx-auto mb-12" data-id="element-1137" data-mp="30qik3hebb338">
            <p className="text-slate-dark text-lg mb-6" data-id="element-1138" data-mp="34pixulhxnfjd">
              At Dr. Imtiaz Syed's practice, we are dedicated to providing
              precise, impartial, and robust psychiatric expertise to legal
              professionals across the UK. We understand the critical role that
              expert insights play in complex legal proceedings, and our
              services are meticulously designed to deliver clarity and
              confidence to your cases.
            </p>
            <p className="text-slate-dark text-lg mb-6" data-id="element-1139" data-mp="2egtllya9rcaq">
              Dr. Syed offers a comprehensive suite of medico-legal psychiatric
              assessments and expert witness services, all delivered with the
              guiding principles of Clarity, Compassion, and Expertise. Each
              assessment is conducted with the utmost care, ensuring
              thoroughness and sensitivity, leading to reports that are not only
              scientifically sound but also practical and directly applicable to
              legal contexts.
            </p>
            <p className="text-slate-dark text-lg" data-id="element-1140" data-mp="1qt2a2wwjz3ai">
              Explore our key areas of specialisation:
            </p>
          </div>
          {/* Services */}
          <div className="space-y-12 mb-16" data-id="element-1141" data-mp="1gttfq2xrzmnm">
            {services.map((service, index) => <div key={index} className="bg-white rounded-lg shadow-md p-8 border-l-4 border-primary" data-id="element-1142" data-mp="3g4ckhr0e6e1m" data-mpi={index}>
                <div className="flex flex-col md:flex-row gap-6" data-id="element-1143" data-mp="19hdf7wdjjxvt">
                  <div className="text-primary md:w-24 flex justify-center" data-id="element-1144" data-mp="19g2ey6jb189g">
                    {service.icon}
                  </div>
                  <div className="flex-1" data-id="element-1145" data-mp="zjhabmb75z65">
                    <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1146" data-mp="zkinbwbrwvqi">
                      {service.title}
                    </h2>
                    <p className="text-slate-dark mb-6" data-id="element-1147" data-mp="1s7v4ts32udcy">
                      {service.description}
                    </p>
                    <Link to={service.link} className="inline-flex items-center text-primary font-medium hover:text-secondary transition-colors" data-id="element-1148" data-mp="1x77v6mt5q2ka">
                      More on {service.title}{' '}
                      <ArrowRight className="ml-1 h-4 w-4" data-id="element-1149" data-mp="1namkplydbz92" />
                    </Link>
                  </div>
                </div>
              </div>)}
          </div>
          {/* Why Choose Section */}
          <div className="bg-gray-50 rounded-lg p-8 shadow-md" data-id="element-1150" data-mp="49u65usq1nit">
            <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-1151" data-mp="4c6vit0t705w">
              Why Choose Dr. Imtiaz Syed for Expert Witness Services?
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1152" data-mp="e78lfr2ws69j">
              Our commitment is to deliver expert medico-legal reports that are:
            </p>
            <ul className="space-y-3 mb-6 text-slate-dark" data-id="element-1153" data-mp="97cg9oih68se">
              <li className="flex items-start" data-id="element-1154" data-mp="3hps5i6bfo5t9">
                <span className="text-secondary font-bold mr-2" data-id="element-1155" data-mp="3cpxem2rnrg87">•</span>
                <span data-id="element-1156" data-mp="3crqi596z5kbz">
                  <strong data-id="element-1157" data-mp="3retgtj719z0w">Robust & Objective:</strong> Clinically sound,
                  evidence-based, and impartial.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1158" data-mp="3mmikeqbi4vd3">
                <span className="text-secondary font-bold mr-2" data-id="element-1159" data-mp="2yy93o2t5axst">•</span>
                <span data-id="element-1160" data-mp="2yweicndyp3o7">
                  <strong data-id="element-1161" data-mp="2tznz5r6v1ryk">Clear & Concise:</strong> Jargon-free and directly
                  relevant to legal questions.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1162" data-mp="28hxky54uhvgm">
                <span className="text-secondary font-bold mr-2" data-id="element-1163" data-mp="2deo1etkkat2k">•</span>
                <span data-id="element-1164" data-mp="2ieae91wha7au">
                  <strong data-id="element-1165" data-mp="2idr6ge0jkzaj">Timely & Responsive:</strong> Delivered efficiently to
                  meet court deadlines.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1166" data-mp="2mdrlo0vgtein">
                <span className="text-secondary font-bold mr-2" data-id="element-1167" data-mp="2rfqp1ar7gbfl">•</span>
                <span data-id="element-1168" data-mp="2we1w5dq487u4">
                  <strong data-id="element-1169" data-mp="2wda1c54cee4w">Ethical & Confidential:</strong> Adhering to the
                  highest professional standards.
                </span>
              </li>
            </ul>
            <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1170" data-mp="363li4s94kht7">
              Ready to Instruct Dr. Syed?
            </h3>
            <p className="text-slate-dark mb-4" data-id="element-1171" data-mp="3a6rm1t8rlwwi">
              We invite legal professionals to explore these service areas in
              more detail via the dedicated subpages or to contact us directly
              to discuss your specific case requirements.
            </p>
            <div className="flex items-center" data-id="element-1172" data-mp="yhtszx2nqsev">
              <span className="mr-2 text-slate-dark" data-id="element-1173" data-mp="yk7m40ds1ujs">Contact Us:</span>
              <a href="mailto:enquiries@medicolegal-psychiatry.co.uk" className="text-primary hover:text-secondary transition-colors" data-id="element-1174" data-mp="uhb0e0sxxibm">
                enquiries@medicolegal-psychiatry.co.uk
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>;
}